import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Skeleton,
  Alert,
  Tooltip,
  Badge,
  Fab,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  TrendingUp as TrendingIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CompleteIcon,
  PlayArrow as ActiveIcon,
  Refresh as RefreshIcon,
  ViewModule as GridIcon,
  ViewList as ListIcon,
  Visibility as ViewIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { useAuth } from '../providers/AuthProvider';
import CampaignCard from '../components/campaigns/CampaignCard';
import CampaignFilters from '../components/campaigns/CampaignFilters';
import CampaignStats from '../components/campaigns/CampaignStats';
import CampaignEmptyState from '../components/campaigns/CampaignEmptyState';
import CampaignDeleteDialog from '../components/campaigns/CampaignDeleteDialog';
import { campaignApi, trackEvent } from '../services/campaignApi';

// Demo campaign data
const DEMO_CAMPAIGN = {
  id: 'demo-campaign-2024',
  name: '🚀 TechFlow Pro Product Launch Campaign',
  websiteUrl: 'https://techflowpro.com',
  campaignGoal: 'product_launch',
  status: 'active',
  businessProfile: {
    businessName: 'TechFlow Pro',
    industry: 'Software Development',
    targetAudience: 'Small to medium businesses, entrepreneurs, tech professionals',
    businessDescription: 'TechFlow Pro is a comprehensive project management and workflow automation platform designed for modern teams. We help businesses streamline their processes, increase productivity, and scale efficiently.',
    location: 'San Francisco, CA'
  },
  totalPosts: 24,
  publishedPosts: 18,
  scheduledPosts: 6,
  draftPosts: 0,
  platforms: ['instagram', 'twitter', 'linkedin', 'facebook'],
  createdAt: '2024-01-15T10:00:00Z',
  updatedAt: '2024-01-20T14:30:00Z',
  startDate: '2024-01-15T00:00:00Z',
  endDate: '2024-02-15T23:59:59Z',
  description: 'Comprehensive product launch campaign for TechFlow Pro v2.0, featuring new AI-powered automation tools, enhanced collaboration features, and mobile app launch.',
  tags: ['product-launch', 'ai-tools', 'productivity', 'saas', 'mobile-app'],
  budget: 15000,
  targetMetrics: {
    impressions: 500000,
    engagement: 25000,
    clicks: 12000,
    conversions: 300,
    revenue: 75000
  },
  currentMetrics: {
    impressions: 425000,
    engagement: 28500,
    clicks: 14200,
    conversions: 342,
    revenue: 89600,
    reach: 320000,
    followers: 2850
  },
  buckets: [
    {
      id: 'bucket-1',
      name: 'Product Announcement',
      description: 'Initial product launch announcements and teasers',
      posts: 6,
      status: 'completed'
    },
    {
      id: 'bucket-2',
      name: 'Feature Highlights',
      description: 'Detailed feature showcases and demos',
      posts: 8,
      status: 'active'
    },
    {
      id: 'bucket-3',
      name: 'Customer Success Stories',
      description: 'User testimonials and case studies',
      posts: 6,
      status: 'ready'
    },
    {
      id: 'bucket-4',
      name: 'Launch Event Promotion',
      description: 'Virtual launch event and webinar promotion',
      posts: 4,
      status: 'scheduled'
    }
  ],
  recentActivity: [
    {
      type: 'post_published',
      message: 'Published "AI-Powered Automation Demo" to LinkedIn',
      timestamp: '2024-01-20T09:15:00Z'
    },
    {
      type: 'post_scheduled',
      message: 'Scheduled 3 posts for Instagram Stories',
      timestamp: '2024-01-19T16:30:00Z'
    },
    {
      type: 'analytics_milestone',
      message: 'Reached 400K impressions milestone',
      timestamp: '2024-01-18T12:00:00Z'
    }
  ],
  isDemo: true
};

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease-in-out',
  boxShadow: theme.customShadows?.card || theme.shadows[1],
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.customShadows?.cardHover || theme.shadows[8],
    borderColor: theme.palette.primary.main,
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(5),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
    alignItems: 'stretch',
  },
}));

const TabsContainer = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
  '& .MuiTabs-root': {
    minHeight: 48,
    background: theme.palette.background.paper,
    paddingLeft: theme.spacing(3), // Match search bar padding
    paddingRight: theme.spacing(3),
  },
  '& .MuiTab-root': {
    minHeight: 48,
    padding: theme.spacing(1.5, 2),
    marginRight: theme.spacing(3),
    transition: 'all 0.2s ease',
    fontWeight: 500,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: theme.palette.text.primary,
    opacity: 0.7,
    '&:last-child': {
      marginRight: 0,
    },
    '&:hover': {
      backgroundColor: 'transparent',
      opacity: 1,
    },
    '&.Mui-selected': {
      color: theme.palette.primary.main,
      fontWeight: 600,
      opacity: 1,
    },
  },
  '& .MuiTabs-indicator': {
    height: 3,
    backgroundColor: theme.palette.primary.main,
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  '& .MuiTabs-flexContainer': {
    justifyContent: 'flex-start',
  },
}));

// Memoized Demo Campaign Card Component
const DemoCampaignCard = React.memo(({ campaign, onViewDemo, onViewAnalytics }) => (
  <Paper
    sx={{
      p: 3,
      mb: 3,
      border: '2px solid',
      borderColor: 'primary.main',
      borderRadius: 2,
      background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.02) 100%)',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        background: 'linear-gradient(90deg, #1976d2, #42a5f5, #90caf9)',
      }
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Chip
            label="DEMO CAMPAIGN"
            size="small"
            color="primary"
            sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
          />
          <Chip
            label={campaign.status.toUpperCase()}
            size="small"
            color="success"
            variant="outlined"
          />
        </Box>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          {campaign.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {campaign.description}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {campaign.tags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />
          ))}
        </Box>
      </Box>
    </Box>

    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={6} sm={3}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
            {campaign.currentMetrics.impressions.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Impressions
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={6} sm={3}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
            {campaign.currentMetrics.engagement.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Engagement
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={6} sm={3}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" color="warning.main" sx={{ fontWeight: 'bold' }}>
            {campaign.totalPosts}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total Posts
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={6} sm={3}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" color="secondary.main" sx={{ fontWeight: 'bold' }}>
            ${campaign.currentMetrics.revenue.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Revenue
          </Typography>
        </Box>
      </Grid>
    </Grid>

    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
      <Button
        variant="contained"
        startIcon={<ViewIcon />}
        onClick={() => onViewDemo(campaign)}
        sx={{ minWidth: 140 }}
      >
        View Campaign
      </Button>
      <Button
        variant="outlined"
        startIcon={<AnalyticsIcon />}
        onClick={() => onViewAnalytics(campaign)}
        sx={{ minWidth: 140 }}
      >
        View Analytics
      </Button>
    </Box>
  </Paper>
));

// Memoized Stats Card Component
const StatCard = React.memo(({ title, value, icon: Icon, color }) => (
  <StatsCard>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <Box>
        <Typography 
          variant="h2" 
          sx={{ 
            color: `${color}.main`,
            fontWeight: 700,
            lineHeight: 1,
            mb: 1
          }}
        >
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          backgroundColor: (theme) => theme.palette[color][50],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon sx={{ fontSize: 28, color: `${color}.main` }} />
      </Box>
    </Box>
  </StatsCard>
));

const CampaignDashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  
  // State
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Separate API filters from UI tab state
  const [apiFilters, setApiFilters] = useState({
    goal: 'all',
    dateRange: 'all'
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [anchorEl, setAnchorEl] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showDemoFirst, setShowDemoFirst] = useState(true);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load campaigns only when API filters change (not tab changes)
  useEffect(() => {
    loadCampaigns();
  }, [apiFilters]);

  // Helper function to show notifications
  const showNotification = useCallback((message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    if (user) {
      trackEvent('campaign_dashboard_viewed', {
        userId: user?.id,
        filtersApplied: Object.values(apiFilters).some(filter => filter !== 'all')
      });
    }
  }, [user, apiFilters]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await campaignApi.listCampaigns({
        ...apiFilters,
        status: 'all' // Always fetch all statuses for client-side filtering
      });
      
      if (response.success) {
        setCampaigns(response.data.results);
      } else {
        throw new Error('Failed to load campaigns');
      }
    } catch (error) {
      console.error('Failed to load campaigns:', error);
      setError(error.message || 'Failed to load campaigns');
      // Set empty campaigns array on error to show demo campaign
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadCampaigns();
    setRefreshing(false);
  }, [apiFilters]);

  const handleSearchChange = useCallback((event) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleFilterChange = useCallback((newFilters) => {
    setApiFilters(newFilters);
  }, []);

  // Optimized tab change handler - no API calls, just UI state
  const handleTabChange = useCallback((event, newValue) => {
    setSelectedTab(newValue);
    
    trackEvent('campaign_tab_changed', {
      userId: user?.id,
      tabIndex: newValue,
      tabName: ['all', 'active', 'ready', 'completed'][newValue]
    });
  }, [user]);

  const handleMenuClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleCreateCampaign = useCallback(() => {
    navigate('/campaigns/create');
    trackEvent('campaign_create_button_clicked', {
      userId: user?.id,
      source: 'dashboard'
    });
  }, [navigate, user]);

  const handleCampaignClick = useCallback((campaign) => {
    navigate(`/campaigns/${campaign.id}/review`);
    trackEvent('campaign_card_clicked', {
      userId: user?.id,
      campaignId: campaign.id,
      campaignStatus: campaign.status
    });
  }, [navigate, user]);

  const handleViewDemo = useCallback((campaign) => {
    navigate(`/campaigns/${campaign.id}/review`);
    trackEvent('demo_campaign_viewed', {
      userId: user?.id,
      campaignId: campaign.id
    });
  }, [navigate, user]);

  const handleViewAnalytics = useCallback((campaign) => {
    navigate(`/campaigns/${campaign.id}/analytics`);
    trackEvent('demo_analytics_viewed', {
      userId: user?.id,
      campaignId: campaign.id
    });
  }, [navigate, user]);

  // Campaign menu action handlers
  const handleEditCampaign = useCallback((campaign) => {
    showNotification(`Opening campaign editor for "${campaign.name}"...`, 'info');
    navigate(`/campaigns/${campaign.id}/edit`);
    trackEvent('campaign_edit_clicked', {
      userId: user?.id,
      campaignId: campaign.id,
      campaignStatus: campaign.status
    });
  }, [navigate, user, showNotification]);

  const handleSchedulePosts = useCallback((campaign) => {
    showNotification(`Opening post scheduler for "${campaign.name}"...`, 'info');
    navigate(`/campaigns/${campaign.id}/schedule`);
    trackEvent('campaign_schedule_clicked', {
      userId: user?.id,
      campaignId: campaign.id,
      campaignStatus: campaign.status
    });
  }, [navigate, user, showNotification]);

  const handleCampaignAnalytics = useCallback((campaign) => {
    showNotification(`Loading analytics for "${campaign.name}"...`, 'info');
    navigate(`/campaigns/${campaign.id}/analytics`);
    trackEvent('campaign_analytics_clicked', {
      userId: user?.id,
      campaignId: campaign.id,
      campaignStatus: campaign.status
    });
  }, [navigate, user, showNotification]);

  const handleDeleteCampaign = useCallback((campaign) => {
    setCampaignToDelete(campaign);
    setDeleteDialogOpen(true);
    
    trackEvent('campaign_delete_dialog_opened', {
      userId: user?.id,
      campaignId: campaign.id,
      campaignStatus: campaign.status
    });
  }, [user]);

  const handleDeleteConfirm = async (campaign) => {
    setIsDeleting(true);
    
    try {
      const response = await campaignApi.deleteCampaign(campaign.id);
      
      if (response.success) {
        // Remove campaign from local state
        setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
        
        // Close dialog and reset state
        setDeleteDialogOpen(false);
        setCampaignToDelete(null);
        
        // Show success notification
        showNotification(`Campaign "${campaign.name}" has been deleted successfully.`, 'success');
        
        trackEvent('campaign_deleted', {
          userId: user?.id,
          campaignId: campaign.id,
          campaignStatus: campaign.status
        });
      } else {
        throw new Error('Failed to delete campaign');
      }
    } catch (error) {
      console.error('Failed to delete campaign:', error);
      showNotification(`Failed to delete campaign: ${error.message}`, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setCampaignToDelete(null);
    
    trackEvent('campaign_delete_dialog_cancelled', {
      userId: user?.id,
      campaignId: campaignToDelete?.id
    });
  }, [user, campaignToDelete]);

  // Memoized filtered campaigns based on search and tab selection
  const filteredCampaigns = useMemo(() => {
    let filtered = campaigns;

    // Filter by search query
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(campaign =>
        campaign.name.toLowerCase().includes(searchLower) ||
        campaign.websiteUrl.toLowerCase().includes(searchLower) ||
        campaign.campaignGoal.toLowerCase().includes(searchLower)
      );
    }

    // Filter by tab selection (client-side)
    const statusMap = ['all', 'active', 'ready', 'completed'];
    const selectedStatus = statusMap[selectedTab];
    
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(campaign => campaign.status === selectedStatus);
    }

    return filtered;
  }, [campaigns, searchQuery, selectedTab]);

  // Memoized campaign statistics
  const allCampaigns = useMemo(() => 
    showDemoFirst ? [DEMO_CAMPAIGN, ...campaigns] : campaigns, 
    [showDemoFirst, campaigns]
  );

  const stats = useMemo(() => ({
    total: allCampaigns.length,
    active: allCampaigns.filter(c => c.status === 'active').length,
    ready: allCampaigns.filter(c => c.status === 'ready').length,
    completed: allCampaigns.filter(c => c.status === 'completed').length,
    totalPosts: allCampaigns.reduce((sum, c) => sum + (c.totalPosts || 0), 0),
    scheduledPosts: allCampaigns.reduce((sum, c) => sum + (c.scheduledPosts || 0), 0),
    publishedPosts: allCampaigns.reduce((sum, c) => sum + (c.publishedPosts || 0), 0)
  }), [allCampaigns]);

  const tabLabels = useMemo(() => [
    { label: 'All Campaigns', count: stats.total },
    { label: 'Active', count: stats.active },
    { label: 'Ready', count: stats.ready },
    { label: 'Completed', count: stats.completed }
  ], [stats]);

  if (loading && campaigns.length === 0) {
    return (
      <StyledContainer maxWidth="lg">
        <HeaderContainer>
          <Skeleton variant="rectangular" width={200} height={40} />
          <Skeleton variant="rectangular" width={150} height={40} />
        </HeaderContainer>
        
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[...Array(4)].map((_, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
          ))}
        </Grid>
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(auto-fill, minmax(300px, 1fr))',
            md: 'repeat(auto-fill, minmax(320px, 1fr))',
            lg: 'repeat(auto-fill, minmax(350px, 1fr))'
          }, 
          gap: { xs: 2, sm: 3 }
        }}>
          {[...Array(6)].map((_, index) => (
            <Skeleton key={index} variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
          ))}
        </Box>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer maxWidth="lg">
      {/* Header */}
      <HeaderContainer>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Campaign Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your social media campaigns and track their performance
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh campaigns">
            <IconButton 
              onClick={handleRefresh} 
              disabled={refreshing}
              color="primary"
            >
              <RefreshIcon sx={{ 
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }} />
            </IconButton>
          </Tooltip>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateCampaign}
            size="large"
          >
            Create Campaign
          </Button>
        </Box>
      </HeaderContainer>

      {/* Demo Campaign Highlight */}
      {showDemoFirst && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              ✨ Explore a Sample Campaign
            </Typography>
            <Button
              size="small"
              onClick={() => setShowDemoFirst(false)}
              sx={{ textTransform: 'none' }}
            >
              Hide Demo
            </Button>
          </Box>
          <DemoCampaignCard
            campaign={DEMO_CAMPAIGN}
            onViewDemo={handleViewDemo}
            onViewAnalytics={handleViewAnalytics}
          />
        </Box>
      )}

      {/* Memoized Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Campaigns"
            value={stats.total}
            icon={TrendingIcon}
            color="primary"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Campaigns"
            value={stats.active}
            icon={ActiveIcon}
            color="success"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Scheduled Posts"
            value={stats.scheduledPosts}
            icon={ScheduleIcon}
            color="warning"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Published Posts"
            value={stats.publishedPosts}
            icon={CompleteIcon}
            color="info"
          />
        </Grid>
      </Grid>

      {/* Search and Filter Bar */}
      <TabsContainer>
        <Box sx={{ p: 3, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flex: 1, maxWidth: 400 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                sx: {
                  height: 40,
                  borderRadius: 1,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 1,
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              startIcon={<FilterIcon sx={{ fontSize: 18 }} />}
              onClick={() => setShowFilters(!showFilters)}
              variant={showFilters ? 'contained' : 'outlined'}
              sx={{ 
                height: 40, // Match search input height exactly
                px: 2,
                fontSize: '0.875rem',
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 'auto',
                borderRadius: 1,
                '& .MuiButton-startIcon': {
                  marginLeft: 0,
                  marginRight: 1,
                }
              }}
            >
              Filters
            </Button>
            
            <Box sx={{ 
              width: 1, 
              height: 24, 
              bgcolor: 'divider', 
              mx: 0.5,
              borderRadius: 0.5 
            }} />
            
            <IconButton
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              size="small"
              sx={{ 
                width: 40,
                height: 40,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                }
              }}
            >
              {viewMode === 'grid' ? <ListIcon sx={{ fontSize: 20 }} /> : <GridIcon sx={{ fontSize: 20 }} />}
            </IconButton>
            
            <IconButton 
              onClick={handleMenuClick}
              size="small"
              sx={{ 
                width: 40,
                height: 40,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                }
              }}
            >
              <MoreIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>

        <Tabs 
          value={selectedTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{
            style: {
              backgroundColor: theme => theme.palette.primary.main,
            }
          }}
        >
          {tabLabels.map((tab, index) => (
            <Tab
              key={index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tab.label}
                  <Box
                    sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 22,
                      minWidth: 22,
                      px: 0.75,
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      backgroundColor: selectedTab === index 
                        ? theme => theme.palette.primary.main 
                        : theme => theme.palette.grey[200],
                      color: selectedTab === index 
                        ? theme => theme.palette.primary.contrastText 
                        : theme => theme.palette.text.primary,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {tab.count}
                  </Box>
                </Box>
              }
            />
          ))}
        </Tabs>
      </TabsContainer>

      {/* Advanced Filters */}
      {showFilters && (
        <CampaignFilters
          filters={apiFilters}
          onChange={handleFilterChange}
          sx={{ mb: 3 }}
        />
      )}

      {/* Campaign List */}
      {filteredCampaigns.length === 0 && !showDemoFirst ? (
        <CampaignEmptyState
          hasSearchQuery={!!searchQuery}
          hasFilters={Object.values(apiFilters).some(filter => filter !== 'all')}
          onCreateCampaign={handleCreateCampaign}
          onClearFilters={() => {
            setSearchQuery('');
            setApiFilters({ goal: 'all', dateRange: 'all' });
            setSelectedTab(0);
          }}
        />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: viewMode === 'grid' 
              ? {
                  xs: '1fr',
                  sm: 'repeat(auto-fill, minmax(300px, 1fr))',
                  md: 'repeat(auto-fill, minmax(320px, 1fr))',
                  lg: 'repeat(auto-fill, minmax(350px, 1fr))'
                }
              : '1fr',
            gap: { xs: 2, sm: 3 },
          }}
        >
          {filteredCampaigns.map((campaign) => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              onClick={() => handleCampaignClick(campaign)}
              viewMode={viewMode}
              onEdit={handleEditCampaign}
              onSchedule={handleSchedulePosts}
              onAnalytics={handleCampaignAnalytics}
              onDelete={handleDeleteCampaign}
            />
          ))}
        </Box>
      )}

      {/* Floating Action Button for Mobile */}
      <Fab
        color="primary"
        aria-label="create campaign"
        onClick={handleCreateCampaign}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'flex', sm: 'none' }
        }}
      >
        <AddIcon />
      </Fab>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>
          Export Campaigns
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          Campaign Analytics
        </MenuItem>
        <MenuItem onClick={() => { setShowFilters(!showFilters); handleMenuClose(); }}>
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </MenuItem>
      </Menu>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Campaign Delete Confirmation Dialog */}
      <CampaignDeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        campaign={campaignToDelete}
        loading={isDeleting}
      />
    </StyledContainer>
  );
};

export default CampaignDashboardPage; 